<template>
  <div class="guides">
    <header class="g-hero">
      <div class="lp-container g-hero__inner">
        <div class="lp-eyebrow"><span class="lp-eyebrow__dot"></span>{{ ui.badge }}</div>
        <h1 class="lp-h1 g-hero__title lp-balance">{{ ui.title }}</h1>
        <p class="lp-lead g-hero__lead lp-balance">{{ ui.lead }}</p>
        <p class="g-hero__count">{{ guides.length }} {{ ui.guides }}</p>
      </div>
    </header>

    <section class="lp-container g-body">
      <content-tabs :tabs="tabs" :label="ui.title" />

      <!-- El plano de la ventana abre el índice: antes de elegir guía, saber qué
           es cada zona ya resuelve la mitad de las dudas. -->
      <guide-figure
        overview
        :labels="zoneLabels"
        :caption="ui.mapCaption"
        :alt="ui.title"
        class="g-map"
      />

      <div v-for="cat in categories" :key="cat.key" :id="`cat-${cat.key}`" class="g-cat">
        <div class="g-cat__head">
          <span class="g-cat__icon"><l-icon size="20" color="primary">{{ cat.icon }}</l-icon></span>
          <div>
            <h2 class="g-cat__name">{{ cat.name }}</h2>
            <p class="g-cat__desc">{{ cat.desc }}</p>
          </div>
        </div>

        <div class="g-grid">
          <router-link
            v-for="g in byCategory[cat.key]"
            :key="g.slug"
            :to="guideLink(g.slug)"
            class="g-card"
          >
            <span class="g-card__icon"><l-icon size="20" color="primary">{{ g.icon }}</l-icon></span>
            <h3 class="g-card__title">{{ g.title }}</h3>
            <p class="g-card__lead">{{ g.lead }}</p>
            <span class="g-card__foot">
              <span class="g-card__go">{{ ui.read }} <l-icon size="14">mdi-arrow-right</l-icon></span>
              <span class="g-card__time">{{ g.minutes }} {{ ui.minutes }}</span>
            </span>
          </router-link>
        </div>
      </div>
    </section>

    <section class="lp-container g-cta-wrap">
      <div class="g-cta">
        <h3 class="lp-h3">{{ ui.ctaTitle }}</h3>
        <p class="g-cta__lead">{{ ui.ctaLead }}</p>
        <router-link class="lp-btn lp-btn--primary lp-btn--lg" :to="langPath('/download')">
          <l-icon small color="white">mdi-download</l-icon>{{ ui.ctaButton }}
        </router-link>
      </div>
    </section>
  </div>
</template>

<script>
import ContentTabs from '@/components/ContentTabs.vue';
import GuideFigure from './GuideFigure.vue';
import {
  GUIDES, CATEGORIES, GUIDE_UI, ZONE_LABELS, guideLang, localizedGuide, localizedCategory,
} from './guides';

export default {
  name: 'GuidesIndex',
  components: { ContentTabs, GuideFigure },
  metaInfo() {
    return {
      title: this.ui.title,
      titleTemplate: '%s | Nukli',
      meta: [
        { name: 'description', content: this.ui.lead },
        { property: 'og:title', content: `${this.ui.title} | Nukli` },
        { property: 'og:description', content: this.ui.lead },
        { property: 'og:type', content: 'website' },
        { property: 'og:url', content: 'https://nukliapp.com/guides' },
      ],
    };
  },
  computed: {
    lang() {
      return guideLang(this.$i18n.locale);
    },
    ui() {
      return GUIDE_UI[this.lang];
    },
    zoneLabels() {
      return ZONE_LABELS[this.lang];
    },
    guides() {
      return GUIDES.map((g) => localizedGuide(g, this.lang));
    },
    categories() {
      return CATEGORIES.map((c) => localizedCategory(c, this.lang));
    },
    byCategory() {
      return this.guides.reduce((acc, g) => {
        (acc[g.category] = acc[g.category] || []).push(g);
        return acc;
      }, {});
    },
    // Los rótulos de las pestañas van en el idioma DEL SITIO (los 19), no en el
    // de las guías: son navegación, y la pestaña "Blog" lleva a contenido que sí
    // está traducido a ese idioma.
    tabs() {
      return [
        { label: this.$t('nav.blog'), to: this.langPath('/blog'), active: false },
        { label: this.$t('nav.guides'), to: this.langPath('/guides'), active: true, count: GUIDES.length },
      ];
    },
  },
  // La vista se carga en diferido, así que cuando el router aplica su
  // scrollBehavior el `#cat-...` puede no existir todavía y el salto se pierde.
  // Al montar ya está en el DOM: aquí se hace de verdad.
  mounted() {
    const hash = this.$route.hash;
    if (!hash) return;
    this.$nextTick(() => {
      const target = document.querySelector(hash);
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  },
  methods: {
    // El sitio monta todas las rutas bajo /:lang? — inglés va sin prefijo.
    langPath(path) {
      const l = this.$route.params.lang;
      return l ? `/${l}${path}` : path;
    },
    guideLink(slug) {
      return this.langPath(`/guides/${slug}`);
    },
  },
};
</script>

<style scoped>
.guides { background: var(--lp-bg); min-height: calc(100vh - 64px); }

.g-hero { padding: 128px 0 8px; text-align: center; }
.g-hero__inner { display: flex; flex-direction: column; align-items: center; gap: 18px; }
.g-hero__title { font-size: clamp(2.4rem, 5vw, 3.6rem); }
.g-hero__lead { max-width: 620px; font-size: 1.18rem; }
.g-hero__count { margin: 0; font-size: 0.9rem; color: var(--lp-muted); }

.g-body { padding: 56px 0 24px; }
.g-map { margin-bottom: 64px; }
/* El AppBar es fijo: sin este margen, el salto a #cat-... deja el título debajo. */
.g-cat { margin-bottom: 64px; scroll-margin-top: 96px; }
.g-cat__head { display: flex; align-items: flex-start; gap: 14px; margin-bottom: 22px; }
.g-cat__icon {
  flex: none;
  width: 42px; height: 42px;
  border-radius: 12px;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--lp-accent-weak);
}
.g-cat__name {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.5rem; font-weight: 600; letter-spacing: -0.02em;
  color: var(--lp-ink); margin: 2px 0 4px;
}
.g-cat__desc { margin: 0; color: var(--lp-muted); font-size: 1.02rem; }

.g-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; }
.g-card {
  display: flex; flex-direction: column;
  background: var(--lp-surface);
  border: 1px solid var(--lp-line);
  border-radius: var(--lp-radius);
  padding: 24px;
  text-decoration: none;
  box-shadow: var(--lp-shadow-sm);
  transition: border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
}
.g-card:hover {
  transform: translateY(-4px);
  border-color: var(--lp-line-strong);
  box-shadow: var(--lp-shadow-md);
}
.g-card__icon {
  width: 40px; height: 40px;
  border-radius: 11px;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--lp-accent-weak);
  margin-bottom: 16px;
}
.g-card__title {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.16rem; font-weight: 600; line-height: 1.3; letter-spacing: -0.01em;
  color: var(--lp-ink); margin: 0 0 10px;
}
.g-card__lead {
  font-size: 1rem; line-height: 1.55; color: var(--lp-muted);
  margin: 0 0 20px; flex: 1;
}
.g-card__foot {
  display: flex; align-items: center; justify-content: space-between;
  padding-top: 16px; border-top: 1px solid var(--lp-line);
}
.g-card__go {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 0.92rem; font-weight: 600; color: var(--lp-accent);
}
.g-card__time { font-size: 0.85rem; color: var(--lp-muted); }

.g-cta-wrap { padding-bottom: 112px; }
.g-cta {
  padding: 40px; text-align: center;
  background: var(--lp-bg-subtle);
  border: 1px solid var(--lp-line);
  border-radius: var(--lp-radius-lg);
  display: flex; flex-direction: column; align-items: center; gap: 10px;
}
.g-cta__lead { color: var(--lp-muted); margin: 0 0 14px; font-size: 1.05rem; }

@media (max-width: 959px) { .g-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 639px) {
  .g-grid { grid-template-columns: 1fr; }
  .g-hero { padding-top: 104px; }
  .g-cta { padding: 28px 20px; }
}
</style>
